// L'état d'une demande en cours de saisie et ce qui le fait bouger (réducteur pur, testé par etatDemande.test.ts). Chaque geste dit ce qu'il a fait
// (`message`) : la page le montre et l'annonce aux lecteurs d'écran. Les fonctions de selection.ts et seancesDemande.ts font le travail.
import { ajouterMois, jourCourt, jourLong, plage, premierDuMois, type DateIso } from './dates';
import {
  appliquerPeriode, basculerPlage, etendre, joursVisibles, nomPeriode, periodes, phrase, phraseAjout, poser, raisonNonSelectionnable, rangPeriodeDe, retirerPeriode,
  selectionnable, type Bout,
} from './selection';
import {
  appliquerGarant, basculerPrevenir, designer, lier, MAX_PREVENUS_PAR_SEANCE, mettreOff, restaurer, type ChoixSeance, type Instantane, type Seance,
} from './seancesDemande';
import { dePrenom, pluriel } from './textesDemande';
import type { Personne } from '../../../types';

export interface EtatDemande {
  absentId: number | null;
  /** La personne choisie juste avant que le champ ne soit vidé (on tape un autre nom) : la retrouver ne remet rien à zéro. */
  absentPrecedent: number | null;
  /** Jours d'absence choisis (lundi→vendredi, jamais le passé), triés. */
  jours: DateIso[];
  /** Dernier jour ajouté : point de départ de « Maj + clic ». */
  ancre: DateIso | null;
  /** Mois affiché, « AAAA-MM-01 ». */
  mois: DateIso;
  /** Le jour de la grille qui reçoit le focus clavier (un seul à la fois). */
  focus: DateIso;
  choix: Record<string, ChoixSeance>;
  off: Record<string, true>;
  /** « + Un autre de vos ateliers ce jour-là » : clés atelier_date des séances ajoutées à la main. */
  ajoutees: string[];
  /** Autre activité ce jour-là : gardée en mémoire même si le jour est retiré, envoyée seulement pour les jours choisis. */
  notesJour: Record<DateIso, string>;
  noteOuverte: DateIso | null;
  flex: boolean;
  motif: 'vacances' | 'autre' | null;
  tipee: boolean;
  tipeeOuvert: boolean;
  notes: string;
  /** La ligne « Du / Au » vide (nouvelle période) : ouverte ou non, et ce qui y est déjà saisi. */
  nouvellePeriode: { ouverte: boolean; du: DateIso | ''; au: DateIso | '' };
  /** Phrase près des dates quand quelque chose n'a pas pu être appliqué. */
  messageDates: string | null;
  /** Ce que le dernier geste a fait (annoncé aux lecteurs d'écran). */
  message: string;
  /** Combien de fois le MÊME message a été redit à la suite : la zone vive ne bouge pas si le texte ne change pas, donc rien ne serait annoncé au 2e geste. */
  repete: number;
  /** Retrait de jours qu'on peut annuler (« Effacer les jours », ✕ d'une période). */
  annulation: { texte: string; jours: DateIso[] } | null;
}

export interface ContexteReducteur {
  aujourdhui: DateIso;
  /** Séances des jours choisis (pour « Aussi pour… »). */
  seances: Seance[];
  personnes: Record<number, Personne>;
}

export type Action =
  | { type: 'absent'; id: number | null; nom: string }
  | { type: 'basculerPlage'; a: DateIso; b: DateIso; valeur?: boolean; clavier?: boolean }
  | { type: 'etendre'; d: DateIso }
  | { type: 'etendreClavier'; de: DateIso; vers: DateIso }
  | { type: 'periode'; index: number | null; du: DateIso | ''; au: DateIso | ''; bout: Bout; /** faux : le calendrier ne change pas de mois (la date est validée par un clic sur ‹ ›) */ voir?: boolean }
  | { type: 'retirerPeriode'; index: number | 'nouvelle' }
  | { type: 'ouvrirNouvellePeriode' }
  | { type: 'brouillonNouvellePeriode'; du: DateIso | ''; au: DateIso | '' }
  | { type: 'effacer' }
  | { type: 'annulerRetrait' }
  | { type: 'mois'; mois: DateIso; focus?: DateIso }
  | { type: 'delta'; delta: number }
  | { type: 'aujourdhui' }
  | { type: 'focus'; d: DateIso }
  | { type: 'message'; texte: string }
  | { type: 'designer'; cle: string; id: number | null }
  | { type: 'retirerRemplacant'; cle: string; versOff: boolean }
  | { type: 'restaurer'; snaps: Instantane[] }
  | { type: 'retirerAussi'; cles: string[] }
  | { type: 'lier'; cle: string; oui: boolean }
  | { type: 'garant'; cle: string; id: number | null }
  | { type: 'prevenir'; cle: string; id: number }
  | { type: 'off'; cle: string; off: boolean }
  | { type: 'ajouterAtelier'; cle: string }
  | { type: 'retirerAtelier'; cle: string }
  | { type: 'noteJour'; date: DateIso; texte: string }
  | { type: 'noteOuverte'; date: DateIso | null }
  | { type: 'flex'; oui: boolean }
  | { type: 'motif'; motif: 'vacances' | 'autre' }
  | { type: 'tipee'; ok: boolean }
  | { type: 'tipeeOuvert' }
  | { type: 'notes'; texte: string };

/** Le jour qui reçoit le focus quand on arrive sur un mois : aujourd'hui s'il y est, sinon le premier jour du mois à l'écran. */
export function focusParDefaut(mois: DateIso, aujourdhui: DateIso): DateIso {
  const vis = joursVisibles(mois);
  if (vis.includes(aujourdhui)) return aujourdhui;
  const apres = vis.find((d) => d >= aujourdhui && d >= mois);
  return apres ?? vis.find((d) => d >= mois) ?? vis[0];
}

export function etatInitial(x: { aujourdhui: DateIso; absentId?: number | null; date?: DateIso | null }): EtatDemande {
  const choisi = x.date && selectionnable(x.date, x.aujourdhui) ? x.date : null;
  const mois = premierDuMois(choisi ?? x.aujourdhui);
  return {
    absentId: x.absentId ?? null, absentPrecedent: null, jours: choisi ? [choisi] : [], ancre: choisi, mois, focus: choisi ?? focusParDefaut(mois, x.aujourdhui),
    choix: {}, off: {}, ajoutees: [], notesJour: {}, noteOuverte: null, flex: false, motif: null, tipee: false, tipeeOuvert: false, notes: '',
    nouvellePeriode: { ouverte: false, du: '', au: '' }, messageDates: null, message: '', repete: 0, annulation: null,
  };
}

/** Montre le mois qui contient ces dates si aucune n'est à l'écran ; sinon ne change rien. */
function afficher(e: EtatDemande, dates: DateIso[], focus: DateIso, aujourdhui: DateIso): Pick<EtatDemande, 'mois' | 'focus'> {
  const vis = joursVisibles(e.mois);
  if (dates.some((d) => vis.includes(d))) return { mois: e.mois, focus };
  const mois = premierDuMois(dates[0]);
  return { mois, focus: joursVisibles(mois).includes(focus) ? focus : focusParDefaut(mois, aujourdhui) };
}

/** Les gestes qui écrivent un message (et peuvent donc le redire à l'identique). */
const DIT_UN_MESSAGE = new Set<Action['type']>(['basculerPlage', 'etendre', 'periode', 'retirerPeriode', 'effacer', 'annulerRetrait', 'message', 'absent', 'prevenir']);

export function reduire(e: EtatDemande, a: Action, ctx: ContexteReducteur): EtatDemande {
  const n = suite(e, a, ctx);
  // Un retrait annulable ne le reste que jusqu'au geste suivant sur les jours (une période réduite par « Du » / « Au » est annulable aussi : voir 'periode').
  if (n.jours !== e.jours && a.type !== 'effacer' && a.type !== 'retirerPeriode' && a.type !== 'periode') n.annulation = null;
  if (n.jours !== e.jours && a.type !== 'periode') n.messageDates = null;
  // Le même message redit (« 1 jour ajouté… » deux fois de suite) : on le marque pour que la zone vive l'annonce de nouveau. Seuls les gestes qui
  // écrivent un message comptent : bouger le focus (flèches) ou taper des précisions laisse l'ancien message en place sans le redire.
  if (n !== e && DIT_UN_MESSAGE.has(a.type) && n.message !== '' && n.message === e.message) return { ...n, repete: e.repete + 1 };
  return n;
}

function suite(e: EtatDemande, a: Action, ctx: ContexteReducteur): EtatDemande {
  const { aujourdhui } = ctx;
  const choixEtat = { choix: e.choix, off: e.off };
  const avecChoix = (c: { choix: Record<string, ChoixSeance>; off: Record<string, true> }, message: string): EtatDemande => ({ ...e, choix: c.choix, off: c.off, message });
  switch (a.type) {
    case 'absent': {
      if (a.id === e.absentId) return e;
      // Le champ de la personne se vide pendant qu'on tape un autre nom : on ne perd rien tant qu'aucune autre personne n'est choisie.
      if (a.id == null) return { ...e, absentId: null, absentPrecedent: e.absentId ?? e.absentPrecedent, message: '' };
      if (a.id === e.absentPrecedent && e.absentId == null) return { ...e, absentId: a.id, absentPrecedent: null, message: '' };
      // Changer de personne : ses choix, son motif, son rappel Tipee et ses autres activités n'ont plus de sens pour la nouvelle ; les jours restent.
      const avaitQuelqueChose = !!(e.motif || e.tipee || e.flex || Object.values(e.notesJour).some(Boolean) || Object.keys(e.choix).length || Object.keys(e.off).length || e.ajoutees.length);
      return {
        ...e, absentId: a.id, absentPrecedent: null, choix: {}, off: {}, ajoutees: [], notesJour: {}, noteOuverte: null, flex: false, motif: null, tipee: false, tipeeOuvert: false,
        message: `Séances ${dePrenom(a.nom)} affichées sur les jours choisis.${avaitQuelqueChose ? ' Motif, rappel Tipee, choix de remplaçant·e et autres activités remis à zéro.' : ''}`,
      };
    }
    case 'basculerPlage': {
      const raison = raisonNonSelectionnable(a.a, aujourdhui, jourLong);
      if (a.a === a.b && raison) return { ...e, focus: a.a, message: raison };
      if (e.absentId == null) return { ...e, focus: a.b, message: "Choisissez d'abord la personne absente." };
      const r = basculerPlage(e.jours, a.a, a.b, aujourdhui, a.valeur);
      const nb = r.plage.length;
      // Au clavier on ne « glisse » pas et on ne clique pas : l'indication parle du geste qu'on est en train de faire.
      const pourUnePeriode = a.clavier ? ' Pour une période : Maj + Espace sur le dernier jour, même dans un autre mois.' : ' Pour une période : glissez, ou Maj (⇧) + clic sur le dernier jour, même dans un autre mois.';
      return {
        ...e, jours: r.jours, ancre: r.valeur ? a.b : null, focus: a.b,
        message: `${pluriel(nb, 'jour')} ${r.valeur ? 'ajouté' : 'retiré'}${nb > 1 ? 's' : ''}.${r.valeur && nb === 1 ? pourUnePeriode : ''}`,
      };
    }
    case 'etendre': {
      const raison = raisonNonSelectionnable(a.d, aujourdhui, jourLong);
      if (raison) return { ...e, focus: a.d, message: raison };
      if (e.absentId == null) return { ...e, focus: a.d, message: "Choisissez d'abord la personne absente." };
      const avant = e.jours;
      const r = etendre(e.jours, e.ancre, a.d, aujourdhui);
      const sansDepart = avant.length === 0 && r.plage.length === 1;
      return { ...e, jours: r.jours, ancre: r.ancre, focus: a.d, message: phraseAjout(r.plage, r.ajoutes.length) + (sansDepart ? ' Maj (⇧) + clic sur un autre jour pour ajouter toute la période.' : '') };
    }
    case 'etendreClavier': {
      if (e.absentId == null) return { ...e, focus: a.vers, message: "Choisissez d'abord la personne absente." };
      const jours = poser(e.jours, plage(a.de, a.vers, true), true, aujourdhui);
      return { ...e, jours, ancre: selectionnable(a.vers, aujourdhui) ? a.vers : e.ancre, focus: a.vers, message: '' };
    }
    case 'periode': {
      if (e.absentId == null) return { ...e, messageDates: "Choisissez d'abord la personne absente.", message: "Choisissez d'abord la personne absente." };
      if (a.index == null && !a.du && a.au) return { ...e, nouvellePeriode: { ...e.nouvellePeriode, au: a.au }, messageDates: "Indiquez aussi la date de début (« Du »).", message: "Indiquez aussi la date de début (« Du »)." };
      if (!a.du) return e;
      const r = appliquerPeriode(e.jours, a.index, a.du, a.au, a.bout, aujourdhui);
      if (r.vide) {
        if (!r.raison) return e;
        return { ...e, messageDates: r.raison, message: r.raison };
      }
      const periode = r.periode!;
      // Le bout tapé est un week-end ou un jour passé et la période n'a pas bougé : on le dit (le champ reprend la date en vigueur), sans annoncer « Période modifiée ».
      const tapee = a.bout === 'du' ? a.du : a.au;
      const raisonTapee = a.index != null && tapee ? raisonNonSelectionnable(tapee, aujourdhui, jourLong) : null;
      if (raisonTapee && !r.ajoutes.length && !r.retires.length) {
        const texte = `${raisonTapee} ${phrase(`La période reste ${nomPeriode(periode)}`)}`;
        return { ...e, messageDates: texte, message: texte };
      }
      const p = plage(periode[0], periode[1], true);
      const base: EtatDemande = { ...e, jours: r.jours, ancre: periode[1], messageDates: null, nouvellePeriode: a.index == null ? { ouverte: false, du: '', au: '' } : e.nouvellePeriode };
      const vu = a.voir === false ? { mois: base.mois, focus: base.focus } : afficher(base, p, e.focus, aujourdhui);
      const ign = r.passes ? 'Les jours passés sont ignorés. ' : '';
      let message: string;
      if (a.index == null) message = ign + phraseAjout(p, r.ajoutes.length) + (periode[0] === periode[1] && a.bout === 'du' ? " Indiquez la fin (« Au ») si l'absence dure plusieurs jours." : '');
      else {
        const rang = rangPeriodeDe(r.jours, periode[0]);
        const voisine = periodes(r.jours)[rang];
        const fondue = voisine && (voisine[0] < periode[0] || voisine[1] > periode[1]);
        message = ign + (fondue ? `Période modifiée ; elle rejoint la période voisine : ${nomPeriode(voisine)} (${pluriel(plage(voisine[0], voisine[1], true).length, 'jour')}).`
          : `Période modifiée : ${nomPeriode(periode)} (${pluriel(p.length, 'jour')}).`);
      }
      if (r.ajustement) message += r.ajustement.bout === 'au' ? ` La fin a été mise au ${jourCourt(r.ajustement.valeur)} : indiquez la nouvelle fin.` : ` ${phrase(`Le début a été mis au ${jourCourt(r.ajustement.valeur)}`)}`;
      // Après une date abrégée (« ven. 4 déc. »), le point de l'abréviation faisait office de point final : « … ven. 4 déc. 10 jours retirés » se lisait comme une date. On sépare.
      if (r.retires.length) message += `${r.ajustement?.bout === 'du' ? ' ·' : ''} ${pluriel(r.retires.length, 'jour')} retiré${r.retires.length > 1 ? 's' : ''}.`;
      // Une période qui rétrécit (« Au » tapé avant « Du » : dix jours deviennent un) retire des jours sans qu'on l'ait demandé jour par jour : « Annuler » les rétablit.
      // Une période qui ne retire rien ne touche pas à un « Annuler » encore valable (effacer les jours, puis taper une date, puis annuler).
      return { ...base, ...vu, message, annulation: r.retires.length ? { texte: message, jours: e.jours } : e.annulation };
    }
    case 'retirerPeriode': {
      if (a.index === 'nouvelle') return { ...e, nouvellePeriode: { ouverte: false, du: '', au: '' }, messageDates: null };
      const r = retirerPeriode(e.jours, a.index);
      if (!r.periode) return e;
      const texte = `Période ${nomPeriode(r.periode)} retirée (${pluriel(r.retires.length, 'jour')}).`;
      return { ...e, jours: r.jours, ancre: null, annulation: { texte, jours: e.jours }, message: texte };
    }
    case 'ouvrirNouvellePeriode': return { ...e, nouvellePeriode: { ouverte: true, du: '', au: '' }, messageDates: null };
    case 'brouillonNouvellePeriode': return { ...e, nouvellePeriode: { ...e.nouvellePeriode, du: a.du, au: a.au } };
    case 'effacer': {
      if (!e.jours.length) return e;
      const texte = `${pluriel(e.jours.length, 'jour')} retiré${e.jours.length > 1 ? 's' : ''}.`;
      return { ...e, jours: [], ancre: null, nouvellePeriode: { ouverte: false, du: '', au: '' }, annulation: { texte, jours: e.jours }, message: texte };
    }
    case 'annulerRetrait': {
      if (!e.annulation) return e;
      const jours = poser(e.jours, e.annulation.jours, true, '0000-01-01');
      const revenus = e.annulation.jours.filter((d) => !e.jours.includes(d)).length;   // seulement ceux qui reviennent (les autres étaient déjà choisis)
      return { ...e, jours, ancre: null, annulation: null, message: `${pluriel(revenus, 'jour')} rétabli${revenus > 1 ? 's' : ''}.` };
    }
    case 'mois': return { ...e, mois: premierDuMois(a.mois), focus: a.focus && joursVisibles(a.mois).includes(a.focus) ? a.focus : focusParDefaut(a.mois, aujourdhui) };
    case 'delta': {
      const mois = ajouterMois(e.mois, a.delta);
      return { ...e, mois, focus: focusParDefaut(mois, aujourdhui) };
    }
    case 'aujourdhui': {
      const mois = premierDuMois(aujourdhui);
      return { ...e, mois, focus: focusParDefaut(mois, aujourdhui) };
    }
    case 'focus': return e.focus === a.d ? e : { ...e, focus: a.d };
    case 'message': return { ...e, message: a.texte };
    case 'designer': {
      const p = a.id != null ? ctx.personnes[a.id] ?? null : null;
      return { ...avecChoix(designer(choixEtat, a.cle, p), '') };
    }
    case 'retirerRemplacant': return avecChoix(mettreOff(designer(choixEtat, a.cle, null), a.cle, a.versOff), '');
    case 'restaurer': return avecChoix(restaurer(choixEtat, a.snaps), '');
    case 'retirerAussi': return avecChoix(a.cles.reduce((c, cle) => designer(c, cle, null), choixEtat), '');
    case 'lier': {
      const s = ctx.seances.find((x) => x.cle === a.cle);
      return s ? avecChoix(lier(ctx.seances, choixEtat, s, a.oui), '') : e;
    }
    case 'garant': {
      const s = ctx.seances.find((x) => x.cle === a.cle);
      return s ? avecChoix(appliquerGarant(ctx.seances, choixEtat, s, a.id), '') : e;
    }
    case 'prevenir': {
      const c = basculerPrevenir(choixEtat, a.cle, a.id);
      return c === choixEtat ? { ...e, message: `${MAX_PREVENUS_PAR_SEANCE} personnes au plus peuvent être prévenues personnellement pour une séance.` } : avecChoix(c, '');
    }
    case 'off': return avecChoix(mettreOff(choixEtat, a.cle, a.off), '');
    case 'ajouterAtelier': return e.ajoutees.includes(a.cle) ? e : { ...e, ajoutees: [...e.ajoutees, a.cle] };
    case 'retirerAtelier': {
      const c = { choix: { ...e.choix }, off: { ...e.off } };
      delete c.choix[a.cle]; delete c.off[a.cle];
      return { ...e, ajoutees: e.ajoutees.filter((x) => x !== a.cle), choix: c.choix, off: c.off };
    }
    case 'noteJour': return { ...e, notesJour: { ...e.notesJour, [a.date]: a.texte } };
    case 'noteOuverte': return { ...e, noteOuverte: a.date };
    case 'flex': return { ...e, flex: a.oui };
    case 'motif': return { ...e, motif: a.motif };
    case 'tipee': return { ...e, tipee: a.ok, tipeeOuvert: a.ok ? e.tipeeOuvert : false };
    case 'tipeeOuvert': return { ...e, tipeeOuvert: true };
    case 'notes': return { ...e, notes: a.texte };
  }
}
